/**
 * Component per mostrar i ordenar els lots d'una categoria
 * Component: EmplaDialog.tsx
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 06/04/2020
 * (c) Ajuntament de Manresa - set 2017 - Departament de Sistemes d'Informació
 */

import React from 'react';
import { FC, useState, useCallback, useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TableContainer, Checkbox, FormControlLabel } from '@material-ui/core';
import AppState from '../states/AppState';
import Util from '../config/util';
import { LotOrdLiniaProps, LotOrdLinia } from './LotOrdLinia';
import MapaState from '../states/MapaState';
import withScrolling from 'react-dnd-scrolling';

const ScrollingComponent = withScrolling('div');

interface IPropsLotOrdDialog {
    appState: AppState;
    mpState: MapaState;
    cat: number;
    onClose: () => void;
};

export const LotOrdDialog: FC<IPropsLotOrdDialog> = (props: IPropsLotOrdDialog) => {

    const handleClose = (save: boolean) => {
        if (save && modif) {
            const lots: string = linies.map((linia, i) => linia.lot.toString()).join(',');
            props.appState.setLotsOrd(props.cat, lots, senseOrdre);
        }
        props.mpState.clearLotOrd();
        props.onClose();
    };

    const handleSenseOrdre = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSenseOrdre(event.target.checked);
        setModif(true);
    };

    const getInfo = () => {
        //if (linies.length === 0) { // Per que només faci la crida un cop. Ara ja no cal. si posem useEffect(getInfo, []); al posar [] fa que només s'executi el 1r cop
            const xreq: string = Util.getXMLRequest('GetEleEstatLotsPartitCat', ['Session', 'Usuari', 'EleCod', 'Cat'], [props.appState.usr.sesCod, Util.encodeHTML(props.appState.usr.userId), props.appState.ele.eleCod.toString(), props.cat.toString()]);
            props.appState.req.postRequestRaw(props.appState.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } }).then(resultat => {
                const xresp: string = Util.getXMLResponse('GetEleEstatLotsPartitCat', resultat as string);
                let lins: Array<LotOrdLiniaProps> = [];
                if (xresp) {
                    let maxOrd = 0;
                    let hiHa0 = false;
                    let pos: { pos: number } = { pos: 0 };
                    let xelelot: string = Util.getBetween('elelot>', '</elelot', xresp, pos);
                    while (xelelot) {
                        const lot: number = parseInt(Util.getBetween('lot>', '</lot', xelelot, { pos: 0 }));
                        const descr: string = Util.getBetween('descr>', '</descr', xelelot, { pos: 0 });
                        const fanals: number = parseInt(Util.getBetween('fanals>', '</fanals', xelelot, { pos: 0 }));
                        const ord: number = parseInt(Util.getBetween('ord>', '</ord', xelelot, { pos: 0 }));
                        if (ord > maxOrd) {
                            maxOrd = ord;
                        } else if (ord === 0) {
                            hiHa0 = true;
                        }
                        const geom: string = Util.getBetween('geom>', '</geom', xelelot, { pos: 0 });
                        xelelot = Util.getBetween('elelot>', '</elelot', xresp, pos);
                        lins.push({lot, descr, fanals, geom});
                    }
                    setSenseOrdre(maxOrd === 0);
                    setModif(hiHa0 && maxOrd > 0);
                }
                if (lins.length > 0) {
                    setLinies(lins);
                } else {
                    setLinies([{lot: 0, descr: '', fanals: 0, geom: ''}]);
                }
            });
        //}
    };

    const [linies, setLinies] = useState<Array<LotOrdLiniaProps>>([]);
    const [modif, setModif] = useState<boolean>(false);
    const [senseOrdre, setSenseOrdre] = useState<boolean>(false);
    const [lastLot, setLastLot] = useState<number>(0);
    const [lastLot2, setLastLot2] = useState<number>(0);

    useEffect(getInfo, []);

    const moveLinia = useCallback((dragIndex: number, hoverIndex: number) => {
        let newLinies = [...linies];
        let dragLinia = linies[dragIndex];
        // Ho fem així per l'error del scroll
        if (lastLot === 0){
            setLastLot(dragLinia.lot);
            if (dragLinia.geom) {
                props.mpState.setLotOrd(dragLinia.geom);
            }
        } else {
            dragIndex = linies.findIndex(l => l.lot === lastLot);
            dragLinia = linies[dragIndex];        
        }
        // Semblava que hauria de ser així
        // if (lastLot !== dragLinia.lot) {
        //     setLastLot(dragLinia.lot);
        //     if (dragLinia.geom) {
        //         props.mpState.setLotOrd(dragLinia.geom);
        //     }
        // }
        if (hoverIndex >= 0) {
            let hoverLinia = newLinies[hoverIndex];
            if (lastLot2 !== hoverLinia.lot) {
                setLastLot2(hoverLinia.lot);
                if (hoverLinia.geom) {
                    props.mpState.setLotOrd2(hoverLinia.geom);
                }
            } else if (lastLot2 !== 0) {
                setLastLot2(0);
                props.mpState.clearLotOrd2();
            }
        }
        newLinies.splice(dragIndex, 1);
        newLinies.splice(hoverIndex, 0, dragLinia);
        setLinies(newLinies);
    }, [linies, lastLot, lastLot2, props.mpState]);

    const dropLinia = useCallback(() => {
        setModif(true);
        setLastLot(0);
        setLastLot2(0);
        props.mpState.clearLotOrd();
    }, [props.mpState]);

    const noop = useCallback(() => {
        //
    }, []);

    const renderLinia = useCallback((linia: LotOrdLiniaProps, index: number) => {
        return (
            <LotOrdLinia
                key={linia.lot}
                index={index}
                lot={linia.lot}
                descr={linia.descr}
                fanals={linia.fanals}
                geom={linia.geom}
                moveLinia={props.appState.readOnly || senseOrdre ? noop : moveLinia}
                dropLinia={props.appState.readOnly || senseOrdre  ? noop : dropLinia}
            />
        )
    }, [props.appState.readOnly, senseOrdre, moveLinia, dropLinia, noop]);

    return (<Dialog 
        open={props.appState.lotOrdCat > 0}
        onClose={handleClose}
        maxWidth={'md'}
        fullWidth={false}
        style={{left: 'calc(100% - "10px")', top: '250px'}}
    >
        <DialogTitle id="form-dialog-title">{(modif ? '*' : '') + 'Lots categoria ' + props.cat.toString() + (props.appState.readOnly || senseOrdre ? '' : ' (arrossegar per ordernar)')}</DialogTitle>
        <DialogContent>
            <TableContainer style={{maxHeight: '400px'}}>
                <ScrollingComponent className="container" style={{overflowY: 'auto', height: '400px'}}>
                    {linies.map((linia, i) => renderLinia(linia, senseOrdre ? -1 : i))}
                </ScrollingComponent>
            </TableContainer>
        </DialogContent>
        <DialogActions>
            {!props.appState.readOnly && <FormControlLabel control={<Checkbox checked={senseOrdre} onChange={handleSenseOrdre}/>}label="Sense ordre"/>}
            <Button onClick={() => handleClose(false)} color="secondary">{props.appState.readOnly ? 'Tancar' : 'Cancel·lar'}</Button>
            {!props.appState.readOnly && <Button onClick={() => handleClose(true)} color="primary">Acceptar</Button>}
        </DialogActions>
    </Dialog>);

}

//(!props.appState.readOnly) && 